<script lang="ts" setup>
import { DynDNSToken as DynDNSTokenSchema, type DynDNSToken, type DynDNSCreateResponse } from '@/types'
import { STATUS_CODES } from '@/constants'
import { DYNDNS_MAX_TOKENS_PER_DOMAIN } from '@/constants/dyndns'
import { computed, onMounted, reactive, ref, defineAsyncComponent } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid, usePermissions } from '@/composables'
import { useNotifyStore } from '@/stores'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'

// Async imports to avoid SSR hydration issues with dynamic placeholders
const DynDnsCreateModal = defineAsyncComponent(() => import('./DynDnsCreateModal.vue'))
const DynDnsTokenCreatedModal = defineAsyncComponent(() => import('./DynDnsTokenCreatedModal.vue'))
const DynDnsDeleteModal = defineAsyncComponent(() => import('./DynDnsDeleteModal.vue'))

const { t } = useI18n()

const props = defineProps<{
  domainId: number
  domainName: string
  isAdmin?: boolean
}>()

const notifyStore = useNotifyStore()
const { canOnDomain, isAdmin: isGlobalAdmin } = usePermissions()

// Permission checks for the domain
const canWrite = computed(() => props.isAdmin || canOnDomain(props.domainId, 'dyndns:write').value || isGlobalAdmin.value)

const data = reactive({
  tokens: [] as DynDNSToken[],
  loading: false
})

const collapsed = ref(false)
const showCreateModal = ref(false)
const showTokenCreatedModal = ref(false)
const showDeleteModal = ref(false)
const createdToken = ref<string | null>(null)
const createdTokenInfo = ref<DynDNSToken | null>(null)
const isRotation = ref(false)
const tokenToDelete = ref<DynDNSToken | null>(null)
const rotatingTokenId = ref<number | null>(null)

const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return t('dashboard.domain.dyndns.neverUpdated')
  const dt = DateTime.fromISO(dateString)
  return dt.toRelative({ locale: 'de' }) || dateString
}

const getRecordTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    A: t('dashboard.domain.dyndns.recordTypes.A'),
    AAAA: t('dashboard.domain.dyndns.recordTypes.AAAA'),
    BOTH: t('dashboard.domain.dyndns.recordTypes.BOTH')
  }
  return labels[type] || type
}

const formatHostname = (hostname: string): string => {
  if (!hostname || hostname === '' || hostname === '@') {
    return props.domainName
  }
  return `${hostname}.${props.domainName}`
}

const isWildcardHostname = (hostname: string): boolean => hostname === '*'

const currentIps = (token: DynDNSToken): string => {
  const ips: string[] = []
  if (token.last_ipv4) ips.push(token.last_ipv4)
  if (token.last_ipv6) ips.push(token.last_ipv6)
  return ips.length > 0 ? ips.join(' / ') : t('dashboard.domain.dyndns.noIp')
}

const formatFutureRelativeTime = (dateString: string): string => {
  const dt = DateTime.fromISO(dateString)
  return dt.toRelative({ locale: 'de' }) || dateString
}

const getStatusInfo = (token: DynDNSToken): { label: string; color: string; icon: string } | null => {
  if (!token.last_error) return null

  switch (token.last_error) {
    case 'rate_limited':
      return {
        label: token.next_allowed_update
          ? t('dashboard.domain.dyndns.status.rateLimitedUntil', {
              time: formatFutureRelativeTime(token.next_allowed_update)
            })
          : t('dashboard.domain.dyndns.status.rateLimited'),
        color: 'warning',
        icon: 'mdi-clock-alert'
      }
    case 'invalid_ip':
      return {
        label: t('dashboard.domain.dyndns.status.invalidIp'),
        color: 'error',
        icon: 'mdi-alert'
      }
    case 'dns_error':
      return {
        label: t('dashboard.domain.dyndns.status.dnsError'),
        color: 'error',
        icon: 'mdi-alert'
      }
    default:
      return {
        label: token.last_error,
        color: 'error',
        icon: 'mdi-alert'
      }
  }
}

const apiBase = computed(() => props.isAdmin ? `/admin/domain/${props.domainId}` : `/domain/${props.domainId}`)

const fetchTokens = () => {
  data.loading = true
  useAxios<null, DynDNSToken[]>('GET', `${apiBase.value}/dyndns`, true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = DynDNSTokenSchema.array().safeParse(res.data)
        if (!validate.success) return
        data.tokens = Array.from(validate.data)
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'DynDnsTokens' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('errors.loadFailed'),
        message: getErrorMessage(error)
      })
    })
    .finally(() => {
      data.loading = false
    })
}

const atTokenLimit = computed(() => data.tokens.length >= DYNDNS_MAX_TOKENS_PER_DOMAIN)

const openCreateModal = () => {
  showCreateModal.value = true
}

const handleTokenCreated = (response: DynDNSCreateResponse) => {
  showCreateModal.value = false
  createdToken.value = response.token
  createdTokenInfo.value = response.info
  isRotation.value = false
  showTokenCreatedModal.value = true
  data.tokens = [...data.tokens, response.info]
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'success',
    title: t('dashboard.domain.dyndns.notifications.createSuccess'),
    message: formatHostname(response.info.hostname)
  })
}

const handleRotateToken = async (token: DynDNSToken) => {
  rotatingTokenId.value = token.id

  try {
    const res = await useAxios<null, DynDNSCreateResponse>(
      'POST',
      `/domain/${props.domainId}/dyndns/${token.id}/rotate`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      createdToken.value = res.data.token
      createdTokenInfo.value = res.data.info
      isRotation.value = true
      showTokenCreatedModal.value = true
      data.tokens = data.tokens.map((t) => (t.id === token.id ? res.data!.info : t))
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.domain.dyndns.notifications.rotateSuccess'),
        message: formatHostname(token.hostname)
      })
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'DynDnsTokens:rotate' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    rotatingTokenId.value = null
  }
}

const openDeleteModal = (token: DynDNSToken) => {
  tokenToDelete.value = token
  showDeleteModal.value = true
}

const handleTokenDeleted = (token: DynDNSToken) => {
  data.tokens = data.tokens.filter((t) => t.id !== token.id)
  showDeleteModal.value = false
  tokenToDelete.value = null
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'success',
    title: t('dashboard.domain.dyndns.notifications.deleteSuccess'),
    message: formatHostname(token.hostname)
  })
}

const closeTokenCreatedModal = () => {
  showTokenCreatedModal.value = false
  createdToken.value = null
  createdTokenInfo.value = null
  isRotation.value = false
}

onMounted(() => {
  fetchTokens()
})
</script>

<template>
  <div class="dyndns-tokens-container" :class="{ collapsed }">
    <!-- Section Header -->
    <div class="section-header" @click="collapsed = !collapsed">
      <div class="header-left">
        <button class="collapse-toggle" :class="{ collapsed }">
          <v-icon icon="mdi-chevron-down" size="20" />
        </button>
        <div class="header-icon">
          <v-icon icon="mdi-ip-network" size="18" />
        </div>
        <div class="header-text">
          <h2 class="section-title">{{ $t('dashboard.domain.dyndns.title') }}</h2>
          <p class="section-subtitle">{{ $t('dashboard.domain.dyndns.subtitle') }}</p>
        </div>
        <v-chip v-if="data.tokens.length > 0" size="small" variant="tonal" class="count-badge" :class="{ 'at-limit': atTokenLimit }">
          {{ data.tokens.length }}/{{ DYNDNS_MAX_TOKENS_PER_DOMAIN }}
        </v-chip>
      </div>
      <div v-if="canWrite" class="header-actions" @click.stop>
        <button
          class="action-btn primary"
          :disabled="atTokenLimit"
          :title="atTokenLimit ? $t('dashboard.domain.dyndns.notifications.maxTokensReached', { limit: DYNDNS_MAX_TOKENS_PER_DOMAIN }) : ''"
          @click="openCreateModal"
        >
          <v-icon icon="mdi-plus" size="18" />
          <span class="btn-text">{{ $t('dashboard.domain.dyndns.createToken') }}</span>
        </button>
      </div>
    </div>

    <!-- Collapsible Content -->
    <div v-show="!collapsed" class="collapsible-content">
      <!-- Loading State -->
      <div v-if="data.loading" class="loading-state">
        <v-progress-circular indeterminate size="32" width="2" color="primary" />
        <span>{{ $t('common.loading') }}</span>
      </div>

      <!-- Tokens Table -->
      <div v-else-if="data.tokens.length > 0" class="tokens-table-wrapper">
        <table class="tokens-table">
          <thead>
            <tr>
              <th class="col-hostname">{{ $t('dashboard.domain.dyndns.columns.hostname') }}</th>
              <th class="col-type">{{ $t('dashboard.domain.dyndns.columns.type') }}</th>
              <th class="col-ip">{{ $t('dashboard.domain.dyndns.columns.currentIp') }}</th>
              <th class="col-update">{{ $t('dashboard.domain.dyndns.columns.lastUpdate') }}</th>
              <th class="col-status">{{ $t('dashboard.domain.dyndns.columns.status') }}</th>
              <th v-if="canWrite" class="col-actions">{{ $t('dashboard.domain.dyndns.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="token in data.tokens" :key="token.id" class="token-row">
              <td class="col-hostname">
                <div class="hostname-cell">
                  <v-tooltip v-if="isWildcardHostname(token.hostname)" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        size="x-small"
                        color="warning"
                        variant="flat"
                        class="wildcard-badge"
                      >
                        <v-icon icon="mdi-asterisk" size="12" start />
                        Wildcard
                      </v-chip>
                    </template>
                    {{ $t('dashboard.domain.dyndns.wildcardTooltip') }}
                  </v-tooltip>
                  <code>{{ formatHostname(token.hostname) }}</code>
                  <span v-if="token.description" class="token-description">· {{ token.description }}</span>
                </div>
              </td>
              <td class="col-type">
                <v-chip size="small" variant="tonal" color="blue">
                  {{ getRecordTypeLabel(token.record_type) }}
                </v-chip>
              </td>
              <td class="col-ip">
                <span class="ip-text">{{ currentIps(token) }}</span>
              </td>
              <td class="col-update">
                <span class="update-time">{{ formatRelativeTime(token.last_update) }}</span>
              </td>
              <td class="col-status">
                <v-chip
                  v-if="getStatusInfo(token)"
                  size="small"
                  variant="tonal"
                  :color="getStatusInfo(token)!.color"
                  :prepend-icon="getStatusInfo(token)!.icon"
                >
                  {{ getStatusInfo(token)!.label }}
                </v-chip>
              </td>
              <td v-if="canWrite" class="col-actions">
                <button
                  class="icon-btn"
                  :title="$t('dashboard.domain.dyndns.rotate.tooltip')"
                  :disabled="rotatingTokenId === token.id"
                  @click="handleRotateToken(token)"
                >
                  <v-progress-circular v-if="rotatingTokenId === token.id" indeterminate size="18" width="2" />
                  <v-icon v-else icon="mdi-refresh" size="18" />
                </button>
                <button
                  class="icon-btn danger"
                  :title="$t('common.delete')"
                  @click="openDeleteModal(token)"
                >
                  <v-icon icon="mdi-trash-can" size="18" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <v-icon icon="mdi-ip-network-outline" size="40" />
        </div>
        <h3 class="empty-title">{{ $t('dashboard.domain.dyndns.empty') }}</h3>
        <p class="empty-text">{{ $t('dashboard.domain.dyndns.emptyDescription') }}</p>
      </div>

      <!-- Limit Notice -->
      <p v-if="canWrite && atTokenLimit" class="limit-notice">
        <v-icon icon="mdi-information-outline" size="14" />
        {{ $t('dashboard.domain.dyndns.notifications.maxTokensReached', { limit: DYNDNS_MAX_TOKENS_PER_DOMAIN }) }}
      </p>
    </div>

    <!-- Create Modal (v-dialog handles its own Teleport) -->
    <DynDnsCreateModal
      :show="showCreateModal"
      :domain-id="domainId"
      :domain-name="domainName"
      @close="showCreateModal = false"
      @created="handleTokenCreated"
    />

    <!-- Token Created Modal (uses v-dialog which handles its own Teleport) -->
    <DynDnsTokenCreatedModal
      v-if="createdToken && createdTokenInfo"
      :show="showTokenCreatedModal"
      :token="createdToken"
      :token-info="createdTokenInfo"
      :domain-name="domainName"
      :is-rotation="isRotation"
      @close="closeTokenCreatedModal"
    />

    <!-- Delete Modal (uses v-dialog which handles its own Teleport) -->
    <DynDnsDeleteModal
      v-if="tokenToDelete"
      :show="showDeleteModal"
      :token="tokenToDelete"
      :domain-id="domainId"
      :domain-name="domainName"
      @close="showDeleteModal = false"
      @deleted="handleTokenDeleted"
    />
  </div>
</template>

<style lang="scss" scoped>
.dyndns-tokens-container {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.03);
  }

  .collapsed & {
    border-bottom: none;
  }
}

.collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 4px;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.8);
  }

  .v-icon {
    transition: transform 0.2s ease;
  }

  &.collapsed .v-icon {
    transform: rotate(-90deg);
  }
}

.count-badge {
  margin-left: 12px;

  &.at-limit {
    color: #ffb74d;
  }
}

.limit-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 12px 24px 20px;
  font-size: 0.8rem;
  color: rgba(255, 183, 77, 0.85);
}

.collapsible-content {
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08));
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 10px;
  color: #81c784;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.section-subtitle {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.primary {
    background: rgba(76, 175, 80, 0.15);
    border: 1px solid rgba(76, 175, 80, 0.25);
    color: #81c784;

    &:hover:not(:disabled) {
      background: rgba(76, 175, 80, 0.25);
      border-color: rgba(76, 175, 80, 0.4);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  color: rgba(200, 218, 255, 0.6);
}

.tokens-table-wrapper {
  overflow-x: auto;
}

.tokens-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 10px 12px;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(15, 23, 48, 0.3);
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  }

  .col-hostname { width: 25%; }
  .col-type { width: 12%; }
  .col-ip { width: 20%; }
  .col-update { width: 15%; }
  .col-status { width: 16%; }
  .col-actions { width: 12%; text-align: right; }
}

.token-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  td {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }
}

.hostname-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.wildcard-badge {
  margin-right: 8px;
}

.token-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

code {
  background: rgba(120, 163, 255, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: rgba(200, 218, 255, 0.9);
}

.ip-text {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.update-time {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }

  &.danger:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 24px;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(120, 163, 255, 0.08);
  border-radius: 18px;
  color: rgba(200, 218, 255, 0.3);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px;
}

.empty-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
  max-width: 320px;
}

/* Light Theme */
.v-theme--lightTheme {
  .dyndns-tokens-container {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .section-title, .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-subtitle, .empty-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .tokens-table th {
    color: rgba(15, 23, 42, 0.6);
    background: rgba(15, 23, 42, 0.03);
  }

  .token-row td {
    color: rgba(15, 23, 42, 0.85);
  }

  code {
    background: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.9);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .header-actions {
    .action-btn {
      padding: 8px 12px;
    }
  }

  .tokens-table {
    .col-ip, .col-update, .col-status {
      display: none;
    }
  }
}
</style>
